import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Building2, ArrowUpDown, DoorClosed, BedDouble } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SgPabellones() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const [habModal, setHabModal] = useState(null); // { pabellon_nombre }
  const [expandedHabs, setExpandedHabs] = useState({}); // { pabellon_id: [habs] }

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/servicios-generales/pabellones`, { headers });
      setRows(r.data);
    } catch (_) { /* noop */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const del = async (p) => {
    if (!window.confirm(`Eliminar el pabellón "${p.nombre}"?`)) return;
    try {
      await axios.delete(`${API}/servicios-generales/pabellones/${p.id}`, { headers });
      toast.success('Eliminado.'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleHabs = async (p) => {
    if (expandedHabs[p.id]) {
      setExpandedHabs(prev => { const n = { ...prev }; delete n[p.id]; return n; });
      return;
    }
    try {
      const r = await axios.get(`${API}/servicios-generales/habitaciones`, { headers });
      const habs = (r.data || []).filter(h => h.edificio === p.nombre)
        .sort((a, b) => String(a.numero).localeCompare(String(b.numero), 'es', { numeric: true }));
      setExpandedHabs(prev => ({ ...prev, [p.id]: habs }));
    } catch (e) { toast.error('Error al cargar habitaciones'); }
  };

  return (
    <div className="space-y-4" data-testid="sg-pabellones-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pabellones</h1>
          <p className="text-sm text-slate-500">Catálogo de pabellones, pisos o áreas donde se ubican las habitaciones.</p>
        </div>
        <button onClick={() => setModal({ mode: 'create' })}
          className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold rounded-lg"
          data-testid="btn-nuevo-pabellon">
          <Plus className="w-4 h-4" /> Nuevo pabellón
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr>
              <th className="text-center px-3 py-2 font-semibold w-14"><ArrowUpDown className="w-3 h-3 inline" /></th>
              <th className="text-left px-4 py-2 font-semibold">Nombre</th>
              <th className="text-left px-4 py-2 font-semibold">Descripción</th>
              <th className="text-center px-4 py-2 font-semibold">Habitaciones</th>
              <th className="text-center px-4 py-2 font-semibold">Activo</th>
              <th className="text-right px-4 py-2 font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-slate-400 py-8">Sin pabellones registrados.</td></tr>
            ) : rows.map(p => (
              <React.Fragment key={p.id}>
                <tr className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="px-3 py-2 text-center text-slate-400 text-xs">{p.orden}</td>
                  <td className="px-4 py-2 font-bold text-slate-800">
                    <button onClick={() => toggleHabs(p)} className="flex items-center gap-2 hover:text-emerald-800" data-testid={`btn-expandir-${p.id}`}>
                      <Building2 className="w-4 h-4 text-emerald-700" />
                      {p.nombre}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{p.descripcion || '—'}</td>
                  <td className="px-4 py-2 text-center">
                    <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold">{p.habitaciones}</span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {p.activo
                      ? <span className="text-[10px] font-bold text-emerald-700">SÍ</span>
                      : <span className="text-[10px] font-bold text-slate-400">NO</span>}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setHabModal({ pabellon_nombre: p.nombre })}
                      className="inline-flex items-center gap-1 px-2 py-1 mr-1 bg-emerald-700 hover:bg-emerald-800 text-white text-[11px] font-semibold rounded-md"
                      title="Agregar habitación a este pabellón"
                      data-testid={`btn-agregar-hab-${p.id}`}>
                      <DoorClosed className="w-3.5 h-3.5" /> Habitación
                    </button>
                    <button onClick={() => setModal({ mode: 'edit', data: p })} className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(p)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
                {expandedHabs[p.id] && (
                  <tr>
                    <td colSpan={6} className="bg-slate-50/60 px-6 py-3 border-t border-slate-100">
                      {expandedHabs[p.id].length === 0 ? (
                        <div className="text-xs text-slate-400 italic">Este pabellón no tiene habitaciones. Usa el botón &ldquo;Habitación&rdquo; para agregar la primera.</div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                          {expandedHabs[p.id].map(h => (
                            <div key={h.id} className="group inline-flex items-center gap-1.5 pl-2 pr-1 py-1.5 bg-white border border-slate-200 hover:border-emerald-400 rounded-lg text-xs" data-testid={`hab-chip-${h.numero}`}>
                              <BedDouble className="w-3 h-3 text-emerald-700 shrink-0" />
                              <button
                                onClick={() => setHabModal({ pabellon_nombre: p.nombre, hab: h })}
                                className="font-bold text-slate-800 hover:text-emerald-800 shrink-0"
                                title="Editar habitación"
                                data-testid={`hab-edit-${h.numero}`}>
                                {h.numero}
                              </button>
                              <span className="text-slate-400 truncate flex-1 min-w-0">{h.tipo || 'S/T'} · cap {h.capacidad}</span>
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Eliminar habitación ${h.numero}?`)) return;
                                  try {
                                    await axios.delete(`${API}/servicios-generales/habitaciones/${h.id}`, { headers });
                                    toast.success('Eliminada.');
                                    load();
                                    const r = await axios.get(`${API}/servicios-generales/habitaciones`, { headers });
                                    setExpandedHabs(prev => ({
                                      ...prev,
                                      [p.id]: (r.data || []).filter(x => x.edificio === p.nombre)
                                        .sort((a, b) => String(a.numero).localeCompare(String(b.numero), 'es', { numeric: true })),
                                    }));
                                  } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded shrink-0"
                                title="Eliminar habitación"
                                data-testid={`hab-del-${h.numero}`}>
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {modal && <ModalPab modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} headers={headers} />}
      {habModal && <ModalHabRapida pabellon={habModal.pabellon_nombre} hab={habModal.hab} onClose={() => setHabModal(null)}
        onSaved={async () => {
          const pName = habModal.pabellon_nombre;
          setHabModal(null);
          load();
          // refresca la lista expandida del pabellón afectado
          try {
            const r = await axios.get(`${API}/servicios-generales/habitaciones`, { headers });
            const pid = rows.find(x => x.nombre === pName)?.id;
            if (pid) {
              setExpandedHabs(prev => ({
                ...prev,
                [pid]: (r.data || []).filter(x => x.edificio === pName)
                  .sort((a, b) => String(a.numero).localeCompare(String(b.numero), 'es', { numeric: true })),
              }));
            }
          } catch (_) { /* noop */ }
        }} headers={headers} />}
    </div>
  );
}

function ModalHabRapida({ pabellon, hab, onClose, onSaved, headers }) {
  const isEdit = !!hab;
  const [form, setForm] = useState({
    numero: hab?.numero || '',
    tipo: hab?.tipo || 'Individual',
    capacidad: hab?.capacidad || 1,
    estado: hab?.estado || 'activa',
    observaciones: hab?.observaciones || '',
  });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.numero.trim()) { toast.error('N° obligatorio'); return; }
    try {
      const payload = {
        ...form,
        edificio: pabellon,
        capacidad: Number(form.capacidad),
      };
      if (isEdit) {
        await axios.put(`${API}/servicios-generales/habitaciones/${hab.id}`, payload, { headers });
        toast.success(`Habitación ${form.numero} actualizada.`);
      } else {
        await axios.post(`${API}/servicios-generales/habitaciones`, payload, { headers });
        toast.success(`Habitación ${form.numero} creada en ${pabellon}.`);
      }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold">{isEdit ? 'Editar' : 'Nueva'} habitación</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {isEdit ? 'En el pabellón' : 'Se asignará al pabellón'} <b className="text-emerald-800">{pabellon}</b>
            </p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 font-semibold">N° *</label>
              <input value={form.numero} onChange={e => setForm({ ...form, numero: e.target.value })}
                placeholder="A-101" autoFocus
                className="w-full px-3 py-2 text-sm border rounded-lg" data-testid="input-hab-rapida-numero" />
            </div>
            <div>
              <label className="text-xs text-slate-500 font-semibold">Capacidad</label>
              <input type="number" min="1" value={form.capacidad} onChange={e => setForm({ ...form, capacidad: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg" />
            </div>
            <div>
              <label className="text-xs text-slate-500 font-semibold">Tipo</label>
              <input value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}
                placeholder="Individual, Doble…"
                className="w-full px-3 py-2 text-sm border rounded-lg" />
            </div>
            <div>
              <label className="text-xs text-slate-500 font-semibold">Estado</label>
              <select value={form.estado} onChange={e => setForm({ ...form, estado: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg">
                <option value="activa">Disponible</option>
                <option value="aseo">En aseo</option>
                <option value="mantenimiento">Mantenimiento</option>
                <option value="deshabilitada">Deshabilitada</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold">Observaciones</label>
            <textarea value={form.observaciones} onChange={e => setForm({ ...form, observaciones: e.target.value })} rows={2}
              className="w-full px-3 py-2 text-sm border rounded-lg" />
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg">Cancelar</button>
            <button type="submit" className="px-4 py-2 text-sm bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold" data-testid="btn-guardar-hab-rapida">
              {isEdit ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModalPab({ modal, onClose, onSaved, headers }) {
  const isEdit = modal.mode === 'edit';
  const d = modal.data;
  const [form, setForm] = useState({
    nombre: d?.nombre || '',
    descripcion: d?.descripcion || '',
    orden: d?.orden ?? 0,
    activo: d?.activo ?? true,
  });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) { toast.error('Nombre obligatorio'); return; }
    try {
      const payload = { ...form, orden: Number(form.orden) };
      if (isEdit) await axios.put(`${API}/servicios-generales/pabellones/${d.id}`, payload, { headers });
      else await axios.post(`${API}/servicios-generales/pabellones`, payload, { headers });
      toast.success('Guardado.');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">{isEdit ? 'Editar' : 'Nuevo'} pabellón</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-slate-500 font-semibold">Nombre *</label>
            <input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })}
              placeholder="Pabellón A, Torre 3, Piso 5…"
              className="w-full px-3 py-2 text-sm border rounded-lg" data-testid="input-pab-nombre" />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold">Descripción</label>
            <textarea value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} rows={2}
              placeholder="Notas o ubicación física"
              className="w-full px-3 py-2 text-sm border rounded-lg" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 font-semibold">Orden</label>
              <input type="number" value={form.orden} onChange={e => setForm({ ...form, orden: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg" />
            </div>
            <div className="flex items-end gap-2">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })} />
                Activo
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg">Cancelar</button>
            <button type="submit" className="px-4 py-2 text-sm bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold" data-testid="btn-guardar-pab">
              {isEdit ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
